import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

/**
 * Format for configurable icons
 * @example <caption>Standard Sizes</caption>
 *   "icon": {
 *      "name": "tier_1",
 *      "set": "product",
 *      "size": "medium",
 *      "customSize": null,
 *      "color": "var(--primary)"
 *   }
 * @example <caption>Custom Size</caption>
 *   "icon": {
 *      "name": "tier_1",
 *      "set": "product",
 *      "size": "custom",
 *      "customSize": "114.75rem",
 *      "color": "var(--primary)"
 *   }
 */
export interface UIIconConfig {
  set: 'system' | 'product';
  size: 'small' | 'medium' | 'large' | 'custom';
  custom_size?: string;
  color: string;
}

/**
 * Sets relevant CSS properties for a Design System <icon> based on configuration
 * @example
 *   <icon
 *      [name]="config.icon.name"
 *      [config]="config.icon"
 *      [ui-icon]="config.icon">
 *   </icon>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[ui-icon]',
})
export class UIIconDirective implements AfterViewInit, OnChanges {
  @Input('ui-icon') config!: UIIconConfig;

  get isCustomSize(): boolean {
    return this.config.size === 'custom';
  }

  get sizeValue(): string | undefined {
    return this.isCustomSize ? this.config.custom_size : this.config.size;
  }

  get sizeVariable(): string {
    return `--icon-${this.config.set}-${
      this.isCustomSize ? 'medium' : this.config.size
    }`;
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    this.applyStyles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.applyStyles();
    }
  }

  applyStyles() {
    const el = this.elementRef.nativeElement;
    const flag = 2;

    this.renderer.setStyle(el, 'min-height', this.sizeValue);
    this.renderer.setStyle(el, this.sizeVariable, this.sizeValue, flag);
    this.renderer.setStyle(el, '--icon-color', this.config.color, flag);
  }
}
